'use client';

import { ReactElement } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { DropdownMenu } from '@/shared/components/primitives/dropdown-menu';
import { FacebookIcon } from '@/shared/components/primitives/svg/facebook-icon';
import { EmailIcon } from '@/shared/components/primitives/svg/email-icon';
import { PinterestIcon } from '@/shared/components/primitives/svg/pinterest-icon';
import { TextNavLink } from '@/shared/components/text-nav-link';
import { cn } from '@ecom/helper-react-class-combine';
import { XIcon } from '@/shared/components/primitives/svg/x-icon';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';

type ShareItem = {
  value: string;
  text: string;
  icon: ReactElement;
  href?: string;
};

export const ShareDropdown = ({ className }: { className?: string }) => {
  const iconClass = 'mb-1 w-5 h-5';
  const iconHoverClass =
    'transition-colors group-hover:text-primary group-data-[highlighted]:text-primary';

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const query = searchParams.toString();
  const pageUrl = origin + pathname + (query ? `?${query}` : '');

  const title = typeof document !== 'undefined' ? document.title : '';
  const fbShareUrl = `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
  const emailShareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(pageUrl)}`;
  const xShareUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(pageUrl)}`;
  const pinterestShareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}&description=${encodeURIComponent(title)}`;

  const shareItems: ShareItem[] = [
    {
      value: 'facebook',
      text: 'Facebook',
      icon: <FacebookIcon className={cn(iconClass, iconHoverClass)} aria-hidden />,
      href: fbShareUrl,
    },
    {
      value: 'email',
      text: 'Email',
      icon: <EmailIcon className={cn(iconClass, iconHoverClass)} aria-hidden />,
      href: emailShareUrl,
    },
    {
      value: 'x',
      text: 'X',
      icon: <XIcon className={cn(iconClass, iconHoverClass)} aria-hidden />,
      href: xShareUrl,
    },
    {
      value: 'pinterest',
      text: 'Pinterest',
      icon: <PinterestIcon className={cn(iconClass, iconHoverClass)} aria-hidden />,
      href: pinterestShareUrl,
    },
  ];

  const items = shareItems.map((item) => ({
    value: item.value,
    label: (
      <TextNavLink
        className='font-body-bold flex items-center gap-2 text-base'
        href={item.href ?? ''}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => emitGTMEvent('GAME_SHARED', { game_shared_method: item.value })}
      >
        {item.icon}
        {item.text}
      </TextNavLink>
    ),
  }));

  return (
    <DropdownMenu
      className={className}
      items={items}
      ariaLabel='Share menu'
      align='start'
      menuTitle='Share'
      variant='button'
      triggerClassName={cn(
        'text-text border-white',
        'min-w-36 justify-center',
        'font-body-bold text-lg',
        'hover:text-text-hover hover:border-[var(--color-text-hover)] hover:bg-transparent',
      )}
      contentClassName={cn('bg-background-contrast lg:bg-background-panel max-w-36')}
      itemClassName={cn(
        'group text-text px-4 py-3 text-base',
        'hover:bg-transparent data-[highlighted]:bg-transparent',
      )}
      withArrow={false}
    />
  );
};
